import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import BookDemoModal from "../components/BookDemoModal";
import { useBusy } from "../context/BusyContext";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import "../assets/style.css";
import "../assets/services.css";
import "../styles/softwarePublic.css";

interface SolutionItem {
  title: string;
  image: string | null;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface ModuleDetail {
  id: number;
  software_module_id: number;
  software_module_title: string;
  software_module_description: string;
  software_module_image: string | null;
  module_link: string;
  software_title: string;
  title: string;
  ai_answer_blocks: string[];
  problems: string[];
  solutions: SolutionItem[];
  how_it_works: string[];
  faqs: FaqItem[];
}

const BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";

const IMAGE_URL =
  import.meta.env.VITE_LYRAZONE_IMAGE_URL || "http://localhost:5000/uploads";

const SoftwareModuleDetail: React.FC = () => {
  const { softwareSlug = "" } = useParams();
  const { showModal } = useMessageModal();
  const { runBusy } = useBusy();

  const [data, setData] = useState<ModuleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchSection, setSearchSection] = useState("");
  const [bookDemoOpen, setBookDemoOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const storedUser = JSON.parse(
    localStorage.getItem("subscriber_user") || "null",
  );

  const getSolutionImage = (image: string | null) => {
    if (!image) return "";
    if (image.startsWith("http")) return image;
    return `${IMAGE_URL}/uploads/software-support/${image}`;
  };

  const fetchDetail = async () => {
    try {
      const res = await runBusy(() =>
        fetch(`${BASE_URL}/admin/software/module-page/${softwareSlug}`),
      );
      const result = await res.json();

      if (result.success) {
        setData(result.data);
      }
    } catch (error) {
      console.error("Fetch Module Detail Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [softwareSlug]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPreviewImage(null);
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const handleBookDemo = () => {
    if (!storedUser) {
      showModal({
        type: "warning",
        title: "Warning",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    setBookDemoOpen(true);
  };

  const visibleSections = useMemo(() => {
    const q = searchSection.toLowerCase().trim();

    const sections = [
      { key: "ai", label: "AI Answer Block" },
      { key: "problems", label: "Problems" },
      { key: "solutions", label: "Solutions" },
      { key: "how", label: "How It Works" },
      { key: "faq", label: "FAQ" },
    ];

    if (!q) return sections;

    return sections.filter((item) => item.label.toLowerCase().includes(q));
  }, [searchSection]);

  const isVisible = (key: string) =>
    visibleSections.some((section) => section.key === key);

  const scrollToSection = (key: string) => {
    const section = document.getElementById(key);
    if (!section) return;

    section.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (loading) {
    return (
      <div className="software-detail-loading">Loading software details...</div>
    );
  }

  if (!data) {
    return (
      <div className="software-detail-loading">
        Software module detail not found.
      </div>
    );
  }

  return (
    <>
      <div id="home-p" className="home-p pages-head6 text-center">
        <div className="container"></div>
      </div>

      <section className="software-detail-page container-fluid">
        <div className="row">
          <aside className="col-lg-3 col-md-4">
            <div className="software-filter-box sticky-filter">
              <h4>SEARCH FILTER</h4>

              <div className="software-filter-divider"></div>

              <label>SEARCH SECTIONS</label>
              <input
                type="text"
                placeholder="Search..."
                value={searchSection}
                onChange={(e) => setSearchSection(e.target.value)}
              />

              <div className="software-filter-divider"></div>

              <h5>SECTIONS</h5>

              {visibleSections.map((section) => (
                <div
                  key={section.key}
                  role="button"
                  tabIndex={0}
                  className="software-section-filter-link"
                  onClick={() => scrollToSection(section.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") scrollToSection(section.key);
                  }}
                >
                  {section.label}
                </div>
              ))}
            </div>
          </aside>

          <div className="col-lg-9 col-md-8">
            <div className="software-detail-hero">
              <span>{data.software_title}</span>
              <h1>{data.title}</h1>
              <p>{data.software_module_description}</p>

              <button type="button" onClick={handleBookDemo}>
                Book a Demo
              </button>
            </div>

            {isVisible("ai") && (
              <section id="ai" className="software-detail-section ai-section">
                <div className="software-section-title">
                  <span>AI Overview</span>
                  <h2>AI Answer Block</h2>
                </div>

                <div className="ai-answer-grid">
                  {data.ai_answer_blocks?.map((item, index) => (
                    <div key={index} className="ai-answer-card">
                      <span>{index + 1}</span>
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {isVisible("problems") && (
              <section id="problems" className="software-detail-section">
                <div className="software-section-title">
                  <span>Business Pain Points</span>
                  <h2>Problems</h2>
                </div>

                <ul className="problem-list">
                  {data.problems?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>
            )}

            {isVisible("solutions") && (
              <section id="solutions" className="software-detail-section">
                <div className="software-section-title">
                  <span>Our Solution</span>
                  <h2>Solutions</h2>
                </div>

                <div className="solution-grid">
                  {data.solutions?.map((item, index) => (
                    <div key={index} className="solution-card">
                      {item.image && (
                        <img
                          src={getSolutionImage(item.image)}
                          alt={item.title}
                          className="solution-preview-image"
                          onClick={() =>
                            setPreviewImage(getSolutionImage(item.image))
                          }
                        />
                      )}

                      <div>
                        <span>Solution {index + 1}</span>
                        <h3>{item.title}</h3>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {isVisible("how") && (
              <section id="how" className="software-detail-section">
                <div className="software-section-title">
                  <span>Workflow</span>
                  <h2>How It Works</h2>
                </div>

                <div className="how-it-works-timeline">
                  {data.how_it_works?.map((item, index) => (
                    <div key={index} className="how-step">
                      <div className="how-step-number">{index + 1}</div>
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {isVisible("faq") && (
              <section id="faq" className="software-detail-section">
                <div className="software-section-title">
                  <span>Questions</span>
                  <h2>FAQ</h2>
                </div>

                <div className="faq-list">
                  {data.faqs?.map((item, index) => (
                    <details key={index} className="faq-item">
                      <summary>{item.question}</summary>
                      <p>{item.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}

            <div className="software-detail-bottom-cta">
              <h2>Ready to improve your business process?</h2>
              <p>
                Book a demo with Lyra Zone Solutions and see how this module can
                support your operation.
              </p>
              <button type="button" onClick={handleBookDemo}>
                Book a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      <BookDemoModal
        isOpen={bookDemoOpen}
        onClose={() => setBookDemoOpen(false)}
        software={{
          id: data.software_module_id,
          title: data.software_module_title,
        }}
      />
      {previewImage && (
        <div className="solution-image-fullscreen">
          <button
            type="button"
            className="solution-image-close-btn"
            onClick={() => setPreviewImage(null)}
          >
            ✕
          </button>

          <img
            src={previewImage}
            alt="Preview"
            className="solution-image-fullscreen-img"
          />
        </div>
      )}
    </>
  );
};

export default SoftwareModuleDetail;
